import React, {
  useRef, useMemo, useState, useEffect,
} from 'react';

import classNames from 'classnames';
import type Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { uniqueId } from 'lodash';
import {
  Stage, Layer, Group, Line,
} from 'react-konva';

import { DrawingOverlayPolygon } from '@vlabs/monokit/shared/ui/canvas-items';
import { CLOSE_RADIUS_PX, DEFAULT_COLOR } from '@vlabs/monokit/shared/ui/canvas-items/constants';

import { CANVAS_COLORS } from './colors';
import { ANCHOR_PADDING } from './constants';
import { RegionPolygon } from './RegionPolygon';
import { RegionRectangle } from './RegionRectangle';
import st from './RegionsDrawing.module.sass';
import type { RegionsDrawingProps } from './types';
import type { TPoint, TVertex } from '../../types/regions';

// конвертеры между {x,y} и {x,y,id}
function toVertices(pts: TPoint[] | undefined): TVertex[] {
  return (pts ?? []).map((p) => ({ ...p, id: uniqueId() }));
}
function vtxToPts(vs: readonly TVertex[]): TPoint[] {
  return vs.map(({ x, y }) => ({ x, y }));
}

export function RegionsDrawing({
  className = undefined,
  mediaOriginSize,
  regionsList = undefined,
  activeRegion = undefined,
  onChangeActiveRegion = undefined,
  onChangeRegion = undefined,
  isDrawing = false,
  onChangeDrawingCoords = undefined,
}: RegionsDrawingProps): React.JSX.Element {
  const stageRef = useRef<Konva.Stage | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [drawingPts, setDrawingPts] = useState<TVertex[]>(toVertices([]));
  const [cursor, setCursor] = useState<TPoint | null>(null);

  useEffect(() => {
    if (!isDrawing) setDrawingPts([]);
    const container = stageRef.current?.container();
    if (container) {
      container.style.cursor = isDrawing ? 'crosshair' : 'default';
    }
  }, [isDrawing]);

  useEffect(() => {
    onChangeDrawingCoords?.(drawingPts);
  }, [drawingPts]);

  // курсор рядом с первой точкой (можно замыкать)
  const showCloseHint = isDrawing && drawingPts.length >= 3 && cursor
    ? Math.hypot(cursor.x - drawingPts[0].x, cursor.y - drawingPts[0].y) <= CLOSE_RADIUS_PX
    : false;

  const stageSize = useMemo(() => {
    if (!rootRef.current) return undefined;
    if (!mediaOriginSize || mediaOriginSize.width === 0 || mediaOriginSize.height === 0) return undefined;
    const rootRect = rootRef.current.getBoundingClientRect();
    if (!rootRect) return undefined;

    const scale = Math.min(rootRect.width / mediaOriginSize.width, rootRect.height / mediaOriginSize.height);

    return {
      width: mediaOriginSize.width * scale,
      height: mediaOriginSize.height * scale,
    };
  }, [mediaOriginSize, rootRef]);

  const onMouseDown = (e: KonvaEventObject<MouseEvent>): void => {
    if (!stageRef.current) return;
    const pos = stageRef.current.getPointerPosition();
    if (!pos) return;

    const targetName = e.target?.name?.() ?? '';
    const isBackground = targetName === 'bg-fill' || targetName === 'bg' || targetName === '';

    if (isDrawing && ((showCloseHint
      || (drawingPts.length >= 3
        && Math.hypot(pos.x - drawingPts[0].x, pos.y - drawingPts[0].y) <= CLOSE_RADIUS_PX)))) {
      const coords = vtxToPts(drawingPts);
      onChangeRegion?.({ ...regionsList.at(-1), coords });
      setDrawingPts([]);
      return;
    }

    if (isDrawing && isBackground) {
      setDrawingPts((arr) => [...arr, { x: pos.x, y: pos.y, id: uniqueId() }]);
      return;
    }

    const clickedShape = e.target;

    if (clickedShape.getClassName() === 'Rect' || clickedShape?.getParent()?.name() === 'polygon') {
      e.cancelBubble = true; // Stop event propagation
      return;
    }

    if (clickedShape.getStage()) {
      onChangeActiveRegion?.(null); // Clicked outside the region
    }
  };

  const onMouseLeave = (): void => {
    document.body.style.cursor = 'default';
  };

  const onMouseOver = (e: KonvaEventObject<MouseEvent>): void => {
    e.target.getStage()!.container().style.cursor = 'default';
  };

  const onMouseMove = () => {
    // обновляет cursor (для направляющей и предпросмотра заливки)
    const pos = stageRef.current?.getPointerPosition();
    if (!pos) return;
    setCursor({ x: pos.x, y: pos.y });
  };

  return (
    <div
      className={classNames(st.Root, className)}
      ref={rootRef}
    >
      {!!stageSize && (
        <Stage
          className={st.Stage}
          height={stageSize.height + ANCHOR_PADDING * 2}
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseMove={isDrawing ? onMouseMove : undefined}
          onMouseOver={onMouseOver}
          ref={stageRef}
          style={{
            height: stageSize.height + ANCHOR_PADDING * 2,
            width: stageSize.width + ANCHOR_PADDING * 2,
          }}
          width={stageSize.width + ANCHOR_PADDING * 2}
          x={ANCHOR_PADDING}
          y={ANCHOR_PADDING}
        >
          <Layer>
            <Group>
              {regionsList?.map((region) => {
                if (!region.isVisible) return null;
                switch (region?.figure) {
                  case 'rect':
                    return (
                      <RegionRectangle
                        bbox={{
                          x: ANCHOR_PADDING,
                          y: ANCHOR_PADDING,
                          width: stageSize.width,
                          height: stageSize.height,
                        }}
                        isSelected={activeRegion?.id === region.id}
                        key={region.id}
                        onSelect={() => {
                          onChangeActiveRegion?.(region);
                        }}
                        onUpdate={(shape) => onChangeRegion?.(shape)}
                        rect={region}
                      />
                    );
                  case 'polygon':
                    if (region.coords.length < 3) return null;
                    return (
                      <RegionPolygon
                        color={CANVAS_COLORS[region?.color]?.base}
                        onChange={(coords) => onChangeRegion?.({ ...region, coords })}
                        onSelect={() => onChangeActiveRegion?.(region)}
                        points={region.coords}
                        selected={activeRegion?.id === region.id}
                        TSizeBounds={{
                          x: ANCHOR_PADDING,
                          y: ANCHOR_PADDING,
                          width: stageSize.width,
                          height: stageSize.height,
                        }}
                      />
                    );

                  default:
                    return null;
                }
              })}

              {!!isDrawing && (
                <React.Fragment>
                  {/* фон-кликер во весь Stage */}
                  <Group name="bg">
                    <Line
                      closed
                      fill="rgba(0,0,0,0.001)"
                      listening
                      name="bg-fill"
                      onMouseEnter={(e) => {
                        e.target.getStage()!.container().style.cursor = 'crosshair';
                      }}
                      points={[0, 0, stageSize.width, 0, stageSize.width, stageSize.height, 0, stageSize.height]}
                    />
                  </Group>

                  <DrawingOverlayPolygon
                    color={CANVAS_COLORS[regionsList.at(-1)?.color]?.base ?? DEFAULT_COLOR}
                    cursor={cursor}
                    filled
                    points={drawingPts}
                    showCloseHint={showCloseHint}
                  />
                </React.Fragment>
              )}
            </Group>
          </Layer>
        </Stage>
      )}
    </div>
  );
}
